import { Pressable, View } from "@-/com.npmjs.react-native";
import Animated from "@-/com.npmjs.react-native-reanimated";
import {
  useHanCoinDepositTarget,
  useHanCoinDepositWalletDisplay,
} from "@-/dev.improve.haniverse.components.ui.han-coin-deposit";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  formatHanAmount,
  formatHanMilesAmount,
  HAN_COIN_ICON_CLASS_NAME,
  HAN_MILES_MAX_FARE_SHARE,
  HAN_MILES_PER_HAN,
  type HanWallet,
} from "@-/dev.improve.haniverse.lib.han-currency";
import { useState } from "react";

const EMPTY_WALLET: HanWallet = { dragons: 0, han: 0, hanMiles: 0 };

/**
 * The learner's Han on the left and HanMiles on the right of the map's sheet, level with its drag
 * handle. A tap adds the dragons beside Han and explains how each is earned; the explanation
 * floats below, over the sheet's content, aligned to the left balance. The daily 红包 is opened
 * from the map home's Welcome Back, just below.
 *
 * While the map is on screen (`isDepositTarget`), earned Han lands here rather than in the drawer
 * button: the 红包's coins drop into the Han balance, which bumps and counts up with each one, and
 * HanMiles bump to their new balance once it is banked.
 */
export function ChinaMapSheetWallet({
  isDepositTarget,
  wallet,
}: {
  isDepositTarget: boolean;
  /** Unknown balances remain dashes until the wallet query resolves. */
  wallet: HanWallet | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const hanTarget = useHanCoinDepositTarget("wallet-han", { enabled: isDepositTarget });
  const hanMilesTarget = useHanCoinDepositTarget("wallet-han-miles", { enabled: isDepositTarget });
  const shown = useHanCoinDepositWalletDisplay(wallet ?? EMPTY_WALLET);
  return (
    <View className="h-full flex-row items-center justify-between" pointerEvents="box-none">
      <Pressable
        accessibilityHint={
          expanded ? "Hides the dragons" : "Shows dragons, and how Han and HanMiles are earned"
        }
        accessibilityLabel={
          wallet === null
            ? "Han balance loading"
            : expanded
              ? `${wallet.han} Han, ${wallet.dragons} dragons`
              : `${wallet.han} Han`
        }
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        className="flex-row items-center gap-2.5 active:opacity-60"
        hitSlop={8}
        onPress={() => setExpanded((value) => !value)}
        testID="han-wallet-chip"
      >
        <View className="flex-row items-center gap-1" testID="han-wallet-balance">
          <View ref={hanTarget.ref}>
            <Animated.View style={hanTarget.pulseStyle}>
              <Icon className={HAN_COIN_ICON_CLASS_NAME} name="HanCashCoin" size={14} />
            </Animated.View>
          </View>
          <Text className="text-xs font-medium text-foreground tabular-nums">
            {wallet === null ? "-" : formatHanAmount(shown.han)}
          </Text>
        </View>
        {expanded ? (
          <View className="flex-row items-center gap-0.5" testID="han-wallet-dragons">
            <Icon className="text-foreground" name="HanDragon" size={14} />
            <Text className="text-xs font-medium text-foreground tabular-nums">
              {wallet?.dragons ?? "-"}
            </Text>
          </View>
        ) : null}
      </Pressable>
      <Pressable
        accessibilityHint={
          expanded ? "Hides the dragons" : "Shows dragons, and how Han and HanMiles are earned"
        }
        accessibilityLabel={
          wallet === null ? "HanMiles balance loading" : `${wallet.hanMiles} HanMiles`
        }
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        className="flex-row items-center gap-0.5 active:opacity-60"
        hitSlop={8}
        onPress={() => setExpanded((value) => !value)}
        testID="han-wallet-han-miles"
      >
        <View ref={hanMilesTarget.ref}>
          <Animated.View style={hanMilesTarget.pulseStyle}>
            <Icon className="text-foreground" name="MdFlightTakeoff" size={14} />
          </Animated.View>
        </View>
        <Text className="text-xs font-medium text-foreground tabular-nums">
          {wallet === null ? "-" : formatHanMilesAmount(shown.hanMiles)}
        </Text>
      </Pressable>
      {expanded ? (
        <View
          className="absolute top-full left-0 mt-2 w-72 gap-1 rounded-xs border border-border bg-background px-3 py-2 shadow-xs"
          pointerEvents="none"
          testID="han-wallet-explanation"
        >
          <Text className="text-xs text-muted-foreground">
            Han are earned by studying, never bought: they pay for travel on the map, where fares
            are priced in yuan and 1 Han covers ¥1. Earn 5 Han per review (1 for Again), and a
            红包 every day, opened from Welcome Back when you open the app: Han, HanMiles, a day of
            bonus Han on every review, and every 7th day a dragon 龙, which flies you anywhere you
            have unlocked, instantly.
          </Text>
          <Text className="text-xs text-muted-foreground">
            Every km you travel earns a HanMile. {HAN_MILES_PER_HAN} HanMiles take 1 Han off a
            flight, up to {Math.round(HAN_MILES_MAX_FARE_SHARE * 100)}%
            of its fare; turning back earns none.
          </Text>
        </View>
      ) : null}
    </View>
  );
}
